import React, { useEffect } from "react";
import "assets/styles/index.scss";
import { Routings } from "pages/routes";
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import "./styles/main.scss";
import { useTranslations } from "hooks/useTranslations";
import { useConfigQuery } from "redux/services/config";
import { getCookieDisclaimer } from "utils/cookieDisclaimer";
import { getOrttoConfig, initOrttoOnConsent } from "utils/ortto";
import { DesktopFooter } from "components/Footer/DesktopFooter";
import { MobileFooter } from "components/Footer/MobileFooter";
import { ConfigProvider, ITopLevelDomains } from "@thekilroygroup/shared-ui";

// Loading component
const Loading: React.FC = () => (
  <Box
    sx={{
      position: "absolute",
      width: "100vw",
      padding: 2,
      top: "50%",
      transform: "translateY(-50%)",
      textAlign: "center",
    }}
  >
    <CircularProgress />
  </Box>
);

// Error component
const Error: React.FC<{ errorText: string }> = ({ errorText }) => (
  <Box
    sx={{
      position: "absolute",
      width: "100vw",
      padding: 2,
      top: "50%",
      transform: "translateY(-50%)",
      textAlign: "center",
    }}
  >
    <Typography variant="h2">{errorText}</Typography>
  </Box>
);

export const App: React.FC = React.memo(() => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const {
    data: appConfig,
    isFetching: isFetchingConfig,
    isError: isErrorConfig,
  } = useConfigQuery();

  const { isFetching, isError, errorText, translations, isLoading } =
    useTranslations();

  // Ortto, gated on marketing consent from the CMP injected below. Has to stay
  // above that effect: the consent event fires once, so a listener attached
  // after the CMP has resolved would never hear it.
  useEffect(() => {
    if (appConfig) {
      return initOrttoOnConsent(getOrttoConfig(appConfig.ENVIRONMENT));
    }
  }, [appConfig]);

  // Set inject cookie disclaimer script
  useEffect(() => {
    if (!isFetchingConfig && !isErrorConfig && appConfig) {
      const cookieScript = getCookieDisclaimer(
        appConfig.TOP_LEVEL_DOMAIN as ITopLevelDomains,
      );

      document.head.appendChild(cookieScript);
      return () => {
        document.head.removeChild(cookieScript);
      };
    }
  }, [appConfig]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& > div:first-of-type": {
          flexGrow: 1,
        },
      }}
    >
      {translations && (
        <ConfigProvider locale={translations?.locale} appConfig={appConfig}>
          <Routings />
          {isDesktop && <DesktopFooter />}
          {!isDesktop && <MobileFooter />}
        </ConfigProvider>
      )}
      {!translations && isFetching && <Loading />}
      {isError && !isFetching && !isLoading && <Error errorText={errorText} />}
    </Box>
  );
});
